import { Seo } from '@/components/Seo';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Palette, Share2, FileText, Package, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const logos = [
  { name: 'Icon (Gradient)', file: 'markipilot-icon-gradient', desc: 'Haupticon mit Farbverlauf' },
  { name: 'Icon (Teal)', file: 'markipilot-icon-teal', desc: 'Einfarbiges Teal Icon' },
  { name: 'Icon (Dunkel)', file: 'markipilot-icon-dark', desc: 'Für helle Hintergründe' },
  { name: 'Icon (Weiß)', file: 'markipilot-icon-white', desc: 'Für dunkle Hintergründe' },
  { name: 'Logo Horizontal', file: 'markipilot-logo-horizontal', desc: 'Für dunkle Hintergründe' },
  { name: 'Logo Horizontal (Weiß)', file: 'markipilot-logo-horizontal-white', desc: 'Für helle Hintergründe' },
  { name: 'Logo Gestapelt', file: 'markipilot-logo-stacked', desc: 'Quadratisches Format' },
  { name: 'Logo Gestapelt (Weiß)', file: 'markipilot-logo-stacked-white', desc: 'Für helle Hintergründe' },
];

const socialMedia = [
  { name: 'Facebook Cover', file: 'facebook-cover-820x312', size: '820 × 312 px', platform: 'Facebook' },
  { name: 'LinkedIn Banner', file: 'linkedin-banner-1584x396', size: '1584 × 396 px', platform: 'LinkedIn' },
  { name: 'X/Twitter Header', file: 'x-twitter-header-1500x500', size: '1500 × 500 px', platform: 'X (Twitter)' },
  { name: 'Instagram Post', file: 'instagram-post-1080x1080', size: '1080 × 1080 px', platform: 'Instagram' },
  { name: 'Instagram Story', file: 'instagram-story-1080x1350', size: '1080 × 1350 px', platform: 'Instagram' },
  { name: 'TikTok', file: 'tiktok-1080x1920', size: '1080 × 1920 px', platform: 'TikTok' },
  { name: 'Pinterest Pin', file: 'pinterest-1000x1500', size: '1000 × 1500 px', platform: 'Pinterest' },
];

const colors = [
  { name: 'Teal (Primary)', hex: '#14b8a6', rgb: '20, 184, 166' },
  { name: 'Cyan (Accent)', hex: '#06b6d4', rgb: '6, 182, 212' },
  { name: 'Slate 950 (Background)', hex: '#020617', rgb: '2, 6, 23' },
  { name: 'Slate 900', hex: '#0f172a', rgb: '15, 23, 42' },
  { name: 'Slate 400 (Text)', hex: '#94a3b8', rgb: '148, 163, 184' },
  { name: 'White', hex: '#ffffff', rgb: '255, 255, 255' },
];

export default function BrandAssetsPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Seo
        title="Brand Assets & Logos"
        description="Offizielle marKIpilot Brand Assets: Logos, Icons und Markenfarben der MYSYMP AG zum Download für Presse und Partner."
        path="/brand"
      />
      {/* Navigation - with iPhone Safe Area */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="small" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero - with safe area padding */}
      <div className="pb-12 border-b border-white/5" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 5rem)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
              Brand Assets
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mb-8">
              Alle Logos, Farben und Social Media Vorlagen für marKIpilot. 
              Verfügbar in SVG, PNG und JPEG.
            </p>
            
            {/* Main Download Button */}
            <a 
              href="/markipilot-brand-assets.zip" 
              download="markipilot-brand-assets.zip"
              className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 rounded-xl text-white font-semibold transition-all shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40"
            >
              <Package className="w-6 h-6" />
              <div className="text-left">
                <div className="text-lg">Alle Assets herunterladen</div>
                <div className="text-sm opacity-80">ZIP-Datei (1.8 MB) · 109 Dateien</div>
              </div>
              <Download className="w-5 h-5 ml-2" />
            </a>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-12 space-y-16">
        
        {/* Logos Section */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-teal-400" />
            </div>
            <h2 className="font-display text-2xl font-bold">Logos</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {logos.map((logo, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-teal-500/30 transition-all"
              >
                <div className={`h-32 flex items-center justify-center p-4 ${logo.file.includes('white') || logo.file === 'markipilot-logo-horizontal' ? 'bg-slate-800' : 'bg-slate-900/50'}`}>
                  <img 
                    src={`/brand/logos/${logo.file}.svg`} 
                    alt={logo.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-white text-sm mb-1">{logo.name}</h3>
                  <p className="text-xs text-slate-500 mb-3">{logo.desc}</p>
                  
                  <div className="grid grid-cols-3 gap-1">
                    <a 
                      href={`/brand/logos/${logo.file}.svg`}
                      download={`${logo.file}.svg`}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      SVG
                    </a>
                    <a 
                      href={`/brand/logos/png/${logo.file}-512x512.png`}
                      download={`${logo.file}-512.png`}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      PNG
                    </a>
                    <a 
                      href={`/brand/logos/jpeg/${logo.file}-512x512.jpg`}
                      download={`${logo.file}-512.jpg`}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      JPG
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* All Sizes Info */}
          <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
            <p className="text-sm text-slate-400">
              <strong className="text-white">Verfügbare Größen in der ZIP:</strong> 64px, 128px, 256px, 512px, 1024px
            </p>
          </div>
        </section>

        {/* Colors Section */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Palette className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="font-display text-2xl font-bold">Farbpalette</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {colors.map((color, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
              >
                <div 
                  className="h-20" 
                  style={{ backgroundColor: color.hex }}
                />
                <div className="p-4">
                  <h3 className="font-medium text-white text-sm mb-2">{color.name}</h3>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-slate-400">HEX: <span className="text-white font-mono">{color.hex}</span></span>
                    <span className="text-slate-400">RGB: <span className="text-white font-mono">{color.rgb}</span></span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Social Media Section */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="font-display text-2xl font-bold">Social Media Banner</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {socialMedia.map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-teal-500/30 transition-all"
              >
                <div className="aspect-video bg-slate-900/50 flex items-center justify-center p-4 overflow-hidden">
                  <img 
                    src={`/brand/social/${item.file}.svg`} 
                    alt={item.name}
                    className="max-h-full max-w-full object-contain rounded"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-white text-sm">{item.name}</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-white/5 text-slate-400">{item.platform}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">{item.size}</p>
                  
                  <div className="grid grid-cols-3 gap-1">
                    <a 
                      href={`/brand/social/${item.file}.svg`}
                      download={`${item.file}.svg`}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      SVG
                    </a>
                    <a 
                      href={`/brand/social/png/${item.file}.png`}
                      download={`${item.file}.png`}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      PNG
                    </a>
                    <a 
                      href={`/brand/social/jpeg/${item.file}.jpg`}
                      download={`${item.file}.jpg`}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      JPG
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Direct Links Section */}
        <section className="bg-white/5 rounded-2xl border border-white/10 p-8">
          <h2 className="font-display text-xl font-bold mb-6 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-teal-400" />
            Direkte Download-Links
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Logos */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Logos (PNG 1024px)</h3>
              <ul className="space-y-2">
                {logos.slice(0, 4).map((logo, i) => (
                  <li key={i}>
                    <a 
                      href={`/brand/logos/png/${logo.file}-1024x1024.png`}
                      download
                      className="text-slate-400 hover:text-teal-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      {logo.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Social PNG */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Social Media (PNG)</h3>
              <ul className="space-y-2">
                {socialMedia.map((item, i) => (
                  <li key={i}>
                    <a 
                      href={`/brand/social/png/${item.file}.png`}
                      download
                      className="text-slate-400 hover:text-teal-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Social JPEG */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Social Media (JPEG)</h3>
              <ul className="space-y-2">
                {socialMedia.map((item, i) => (
                  <li key={i}>
                    <a 
                      href={`/brand/social/jpeg/${item.file}.jpg`}
                      download
                      className="text-slate-400 hover:text-teal-400 text-sm flex items-center gap-2 transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Usage Guidelines */}
        <section className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-2xl border border-teal-500/20 p-8">
          <h2 className="font-display text-xl font-bold mb-4">Verwendungshinweise</h2>
          <ul className="space-y-3 text-slate-400 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
              <span><strong className="text-white">SVG:</strong> Ideal für Web und Print – skaliert verlustfrei in jede Größe.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
              <span><strong className="text-white">PNG:</strong> Mit Transparenz – perfekt für Overlays und digitale Medien.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
              <span><strong className="text-white">JPEG:</strong> Kleinere Dateigröße – ideal für E-Mails und schnelles Laden.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
              <span>Das "KI" im Logo sollte immer in der Akzentfarbe (#14b8a6) hervorgehoben werden.</span>
            </li>
          </ul>
        </section>
      </div>

      {/* Footer - with safe area padding */}
      <footer className="border-t border-white/5 py-6" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-slate-500 text-xs">© {new Date().getFullYear()} marKIpilot – MYSYMP AG</span>
            <div className="flex items-center gap-4">
              <Link to="/impressum" className="text-slate-500 hover:text-slate-300 text-xs">Impressum</Link>
              <Link to="/datenschutz" className="text-slate-500 hover:text-slate-300 text-xs">Datenschutz</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
